import { Col, Container, Row, Table } from "react-bootstrap";
import { YesNoRadioOptions } from "../../../models/form/RadioOptions";
import { AutoCol, Select, RadioGroup, RadioOption, Textarea } from "../../../component-library";
import { capitalizeFirst } from "../../../helper-functions";
import { useCurrentCase, useReferralFormData } from "../../../hooks";
import { BLANK } from "../../../config/constants";
import { RenderedFormProps } from "../RenderForm";
import { useMemo } from "react";
import { FormSlugs, FormDataSignature } from "../../../models";
import { useFormDataQuery } from "../../../queries";

const yesNoOptions = YesNoRadioOptions.map<RadioOption>((o) => ({ ...o, value: capitalizeFirst(o.value) }));

export function DonorOmar({ formState }: Readonly<RenderedFormProps>) {
    const { referral, donor, caseId } = useCurrentCase({ includeDetails: true });
    const { region } = useReferralFormData(referral);

    const {
        listQuery: { data: recoveryForms },
    } = useFormDataQuery({ caseId, formSlug: FormSlugs.Recovery });

    const recoveryFormSignature: FormDataSignature | undefined = useMemo(() => {
        const signatures = recoveryForms?.[0]?.signatures ?? [];
        return signatures[signatures.length - 1];
    }, [recoveryForms]);

    const donorNumber = donor?.number;
    const office = region;
    const techName = recoveryFormSignature?.userName ?? BLANK;

    const tissueOptions = [
        { label: "Cornea", value: "Cornea" },
        { label: "Whole Globe", value: "Whole Globe" },
    ];

    return (
        <Container className="p-4">
            <div className="text-center mb-4">
                <h1>Ocular Management Record - Donor</h1>
            </div>
            <div className="mb-4">
                <Row>
                    <Col>Donor Number: {donorNumber}</Col>
                    <Col>Office: {office}</Col>
                    <Col>Tech: {techName}</Col>
                </Row>
            </div>
            <Table bordered>
                <thead>
                    <tr>
                        <th className="col-6"></th>
                        <th className="text-center">OD</th>
                        <th className="text-center">OS</th>
                    </tr>
                    <tr>
                        <th className="col-6">TISSUE TYPE</th>
                        <th className="text-center">
                            <Select
                                className="mb-2"
                                accessor="tissueTypeOd"
                                options={tissueOptions}
                                isDisabled={formState.isReadonly}
                            />
                        </th>
                        <th className="text-center">
                            <Select
                                className="mb-2"
                                accessor="tissueTypeOs"
                                options={tissueOptions}
                                isDisabled={formState.isReadonly}
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className="col-6">In-Process/Field label consistent with donor information</td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="inProcessFieldLabelConsistentWithDonorInformationOD"
                            />
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="inProcessFieldLabelConsistentWithDonorInformationOS"
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="col-6">Indication of wet-ice upon arrival</td>
                        <td className="text-center">
                            <RadioGroup inline options={yesNoOptions} accessor="indicationOfWetIceUponArrivalOD" />
                        </td>
                        <td className="text-center">
                            <RadioGroup inline options={yesNoOptions} accessor="indicationOfWetIceUponArrivalOS" />
                        </td>
                    </tr>
                    <tr>
                        <td className="col-6">Storage solution acceptable (if Optisol, color range acceptable)</td>
                        <td className="text-center">
                            <RadioGroup inline options={yesNoOptions} accessor="preservationMediumAcceptableOD" />
                        </td>
                        <td className="text-center">
                            <RadioGroup inline options={yesNoOptions} accessor="preservationMediumAcceptableOS" />
                        </td>
                    </tr>
                    <tr>
                        <td className="col-6">Shipping container: Secured and absent of leaks.</td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="shippingContainerSecuredAndAbsentOfLeaksOD"
                            />
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="shippingContainerSecuredAndAbsentOfLeaksOS"
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="col-6">
                            Tissue storage container: Absent of leaks and tamper-evident seals intact (if applicable)
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="tissueStorageContainerAbsentOfLeaksAndTamperEvidentSealsIntactOD"
                            />
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="tissueStorageContainerAbsentOfLeaksAndTamperEvidentSealsIntactOS"
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="col-6">Tissue was transported under Quarantine</td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="tissueWasTransportedUnderQuarantineOD"
                            />
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="tissueWasTransportedUnderQuarantineOS"
                            />
                        </td>
                    </tr>
                </tbody>
            </Table>
            <Row>
                <AutoCol>Comments:</AutoCol>
            </Row>
            <Row>
                <Col>
                    <Textarea accessor="donorOmarComments" />
                </Col>
            </Row>
        </Container>
    );
}
